'use client';

/**
 * The trip at a glance, under its name. One line of figures in one type
 * ramp, the counts and what the times assume a quiet line under them:
 *
 *   13h 7m · 1,019 km · $360 [fuel ⌄]
 *   3 stops · 2 days · 1 night · caravan pace
 *   ⏱ 1 day over your 6h limit · Fix            (only when it's true)
 *
 *  • TripStatsStrip  — the figures. The ONLY place the panel says "3 stops".
 *                      While the road route is still being worked out it
 *                      says so (no straight-line number that changes a
 *                      second later). The fuel guess is the third figure;
 *                      its unit is a small chip ("fuel ⌄") that opens how
 *                      it's worked out. One affordance, not two.
 *                      A day over the driver's limit is the plan's biggest
 *                      problem, so it is said here, with the fix beside it.
 *  • SaveStatusChip  — the save state, solid, with a dot: "● Unsaved". The
 *                      tick draws itself once when a save lands.
 *  • TripNameMenu    — the ⌄ beside the trip name: your other saved trips.
 */
import React from 'react';
import type { StoredTrip } from '../../utils/tripStorage';
import { IconCheck, IconChevronDown, IconClock, IconFolder, IconInfo, IconList, IconPencil } from './icons';
import type { TripStat } from './tripStats';
import { Menu, type MenuItem } from './ui';

/**
 * "13 h 6 m". A real space between number and unit (screen readers and
 * search read "13 h"), drawn narrow so it looks like one figure: 13h 6m.
 */
function Figure({ value, unit, tight = false }: { value: string; unit: string; tight?: boolean }) {
  return (
    <span className={`whitespace-nowrap ${tight ? '[word-spacing:-0.24em]' : '[word-spacing:-0.04em]'}`}>
      {value} <span className="text-[0.82em] font-semibold tracking-normal text-[#3f4f68]">{unit}</span>
    </span>
  );
}

/** The separator between figures: a 4px dot with 12px of air each side. */
function Dot() {
  return <span className="inline-block h-1 w-1 flex-none self-center rounded-full bg-tp-faint" aria-hidden="true" />;
}

export function TripStatsStrip({
  stats,
  pending = false,
  basis,
  basisNote,
  warning,
  className = '',
}: {
  stats: TripStat[];
  /** The road route is still on its way: say so instead of guessing. */
  pending?: boolean;
  /** @deprecated Nothing sits at the strip's right end any more. */
  aside?: React.ReactNode;
  /** What the times assume ("caravan pace"). Shown whenever it applies. */
  basis?: string;
  /** Tooltip for `basis`: how the times are worked out. */
  basisNote?: string;
  /**
   * The plan's biggest problem ("1 day over your 6h limit"), with its fix.
   * Shown under the counts, in the warning tone, only when it's true.
   */
  warning?: { text: string; title?: string; onFix?: () => void };
  className?: string;
}) {
  const [explain, setExplain] = React.useState(false);
  const noteId = React.useId();
  if (!stats.length) return null;
  const by = (k: TripStat['key']) => stats.find((s) => s.key === k);
  const stops = by('stops');
  const days = by('days');
  const nights = by('nights');
  const km = by('km');
  const drive = by('drive');
  const fuel = by('fuel');
  const approx = !!(km?.approx || drive?.approx);
  const hasHero = !pending && !!(km || drive);
  return (
    <div
      className={className}
      data-testid="trip-summary"
      aria-label="Trip at a glance"
      role="group"
      {...(pending ? { 'data-pending': '' } : {})}
    >
      <div className="flex min-h-[32px] items-center">
        {hasHero ? (
          <p
            className="m-0 flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1 text-[22px] font-semibold leading-8 tracking-[-0.015em] text-tp-ink tabular-nums max-[379px]:text-[20px]"
            title={approx ? 'Straight-line guess. The road is usually longer.' : undefined}
            data-testid="trip-hero-figures"
          >
            {approx && (
              <span className="text-[17px] font-medium text-tp-faint" aria-label="About">
                ≈
              </span>
            )}
            {drive && (
              <span data-stat="drive" title={drive.label}>
                <Figure value={drive.value} unit={drive.unit} tight />
                {drive.extra && (
                  <>
                    {' '}
                    <Figure value={drive.extra.value} unit={drive.extra.unit} tight />
                  </>
                )}
              </span>
            )}
            {drive && km && <Dot />}
            {km && (
              <span data-stat="km" title={km.label}>
                <Figure value={km.value} unit={km.unit} />
              </span>
            )}
            {fuel && (
              <>
                <Dot />
                {/* The third figure, in the same ramp. A guess, so it explains
                    itself: tap for how it's worked out. */}
                <span data-stat="fuel" className="whitespace-nowrap">
                  {fuel.value}
                </span>{' '}
                {/* Its own flex item, on the numerals' baseline: on a narrow
                    screen the chip wraps to the next line, never squeezed. */}
                  <button
                    type="button"
                    onClick={() => setExplain((v) => !v)}
                    aria-expanded={explain}
                    aria-controls={noteId}
                    aria-label={`${fuel.label} How it’s worked out`}
                    title="How the fuel guess is worked out"
                    className={`-ml-1.5 inline-flex h-7 min-h-0 flex-none items-center gap-0.5 rounded-full border border-solid px-2 text-[13px] font-semibold leading-none tracking-normal transition-colors duration-150 ease-tp-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 [font-family:inherit] ${
                      explain
                        ? 'border-tp-brand/30 bg-tp-brand-soft text-tp-brand'
                        : 'border-tp-line bg-tp-surface text-tp-muted hover:border-tp-line-strong hover:text-tp-ink'
                    }`}
                    data-testid="fuel-stat"
                  >
                    fuel
                    <IconChevronDown
                      className={`h-3.5 w-3.5 flex-none transition-transform duration-220 ease-tp-out ${explain ? 'rotate-180' : ''}`}
                    />
                  </button>
              </>
            )}
          </p>
        ) : pending ? (
          <p className="m-0 flex items-center gap-2.5 text-tp-base text-tp-muted" aria-live="polite" data-testid="route-pending">
            <span
              className="h-4 w-4 flex-none animate-spin rounded-full border-2 border-solid border-tp-line border-t-tp-brand motion-reduce:animate-none"
              aria-hidden="true"
            />
            Working out the road…
          </p>
        ) : null}
      </div>
      <p className="m-0 mt-0.5 min-h-[20px] min-w-0 truncate text-tp-sm leading-5 text-tp-muted tabular-nums" data-testid="trip-counts">
        {[stops, days, nights]
          .filter((s): s is TripStat => !!s)
          .map((s) => `${s.value} ${s.unit}`)
          .join(' · ')}
        {basis && hasHero ? (
          <span title={basisNote} data-testid="trip-basis">
            {' · '}
            {basis}
          </span>
        ) : null}
      </p>
      {warning && !pending && (
        <p
          // #7a4d00 on white: 7.6:1, AA for small text with room to spare.
          className="m-0 mt-2 flex min-h-[32px] min-w-0 items-center gap-2 text-tp-sm font-semibold leading-5 text-[#7a4d00] animate-tp-fade motion-reduce:animate-none"
          role="status"
          title={warning.title}
          data-testid="trip-warning"
        >
          <IconClock className="h-4 w-4 flex-none" />
          <span className="min-w-0 truncate">{warning.text}</span>
          {warning.onFix && (
              <button
                type="button"
                onClick={warning.onFix}
                // A real secondary button: 32px with a mouse, 44px to a thumb.
                className="inline-flex h-8 min-h-0 flex-none items-center rounded-tp border border-solid border-[#d9a650] bg-white px-3.5 text-tp-sm font-semibold leading-none text-[#7a4d00] transition-colors duration-150 ease-tp-out hover:border-[#b9842c] hover:bg-tp-warn-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b9842c]/50 active:translate-y-px [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:px-4 [font-family:inherit]"
                data-testid="trip-warning-fix"
              >
                Fix
              </button>
          )}
        </p>
      )}
      {fuel && explain && !pending && (
        <p
          id={noteId}
          className="m-0 mt-2 flex items-start gap-2 rounded-tp-sm bg-tp-surface px-3 py-2 text-tp-sm text-tp-muted animate-tp-enter motion-reduce:animate-none"
          data-testid="fuel-note"
        >
          <IconInfo className="mt-px h-4 w-4 flex-none text-tp-faint" />
          <span>{fuel.note}</span>
        </p>
      )}
    </div>
  );
}

/**
 * What an account adds, said once, after a guest's first save (the toast's
 * second line). Names only what is really locked. Pure.
 */
export function signInNudgeCopy(): string {
  return 'Sign in to keep this trip on every device and export GPX.';
}

/** "just now" · "4 min ago" · "2 h ago" · "on 12 Oct". Pure. */
export function savedAgoLabel(at: number, now: number = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `on ${new Date(at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`;
}

/** How long a fresh save says "just now" before it settles to a quiet "Saved". */
export const SAVED_FRESH_MS = 5000;

/**
 * True for the first `ms` after `at`. Re-renders once, when it flips, so
 * the chip settles on its own (no ticking clock).
 */
function useFresh(at: number | null | undefined, ms: number): boolean {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!at) return;
    setNow(Date.now());
    const left = at + ms - Date.now();
    if (left <= 0) return;
    const t = window.setTimeout(() => setNow(Date.now()), left + 20);
    return () => window.clearTimeout(t);
  }, [at, ms]);
  return !!at && now - at < ms;
}

/** What the save chip says. Pure. */
export function saveStatusText(
  state: 'saved' | 'unsaved' | 'changed' | 'device',
  { compact = false, fresh = false }: { compact?: boolean; fresh?: boolean } = {}
): string {
  if (state === 'saved') return fresh && !compact ? 'Saved · just now' : 'Saved';
  if (state === 'changed') return compact ? 'Unsaved' : 'Unsaved changes';
  if (state === 'device') return 'On this device';
  return 'Unsaved';
}

/**
 * The save state: a solid chip with a dot, so it reads on any photo and
 * never looks like a disabled control. A save that just landed says so in
 * green with a tick that draws itself ("Saved · just now"); five seconds on
 * it settles to a quiet grey "Saved" and stops competing with the name.
 *
 *   ✓ Saved · just now → ✓ Saved     ● Unsaved     ● Unsaved changes
 *
 * `compact="dot"`: the dot alone (the phone's condensed title bar); the
 * words go to screen readers and the tooltip.
 */
export function SaveStatusChip({
  state,
  savedAt,
  compact = false,
  children,
}: {
  state: 'saved' | 'unsaved' | 'changed' | 'device';
  /** When it was saved (this session); without it the chip just says "Saved". */
  savedAt?: number | null;
  /** Phones: the short form ("Saved", "Unsaved"). 'dot': the dot alone. */
  compact?: boolean | 'dot';
  /** Extra inline content (the guests' "Back up" link). */
  children?: React.ReactNode;
}) {
  const fresh = useFresh(state === 'saved' ? savedAt : null, SAVED_FRESH_MS);
  const text = saveStatusText(state, { compact: !!compact, fresh });
  const dotTone = state === 'saved' ? 'bg-tp-ok' : state === 'device' ? 'bg-tp-brand' : 'bg-[#e8930c]';
  const tip = state === 'saved' && savedAt ? `Saved ${savedAgoLabel(savedAt)}` : text;
  if (compact === 'dot') {
    return (
      <span className="inline-flex h-4 w-4 flex-none items-center justify-center" title={tip} data-state={state} data-testid="trip-save-dot">
        <span aria-hidden="true" className={`h-2 w-2 rounded-full ${dotTone}`} />
        <span className="sr-only">{text}</span>
      </span>
    );
  }
  return (
    <span
      className={`inline-flex h-7 max-w-full flex-none items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-2.5 text-[12px] font-semibold leading-none tabular-nums shadow-[0_1px_2px_rgba(22,35,58,0.12)] ring-1 ring-inset ring-tp-line transition-colors duration-500 ease-tp-out ${
        state === 'saved' ? (fresh ? 'text-tp-ok' : 'text-tp-muted') : 'text-tp-ink'
      } ${fresh ? 'tp-pop' : ''}`}
      role="status"
      title={tip}
      data-state={state}
      {...(fresh ? { 'data-fresh': '' } : {})}
      {...(state === 'saved' ? { 'data-testid': 'trip-save' } : {})}
    >
      {state === 'saved' ? (
        <IconCheck className={`h-3.5 w-3.5 flex-none ${fresh ? 'tp-tick' : ''}`} />
      ) : (
        <span aria-hidden="true" className={`h-2 w-2 flex-none rounded-full ${dotTone}`} />
      )}
      <span className="truncate">{text}</span>
      {children}
    </span>
  );
}

/** The trip-name menu's items: rename, then your other saved trips. Pure. */
export function tripNameMenuItems({
  savedTrips,
  activeTripId,
  onLoadTrip,
  onRename,
  onSeeAll,
}: {
  savedTrips: StoredTrip[];
  activeTripId: string | null;
  onLoadTrip: (id: string) => void;
  onRename: () => void;
  onSeeAll?: () => void;
}): MenuItem[] {
  const others = savedTrips.filter((t) => t.id !== activeTripId).slice(0, 6);
  return [
    { key: 'rename', label: 'Rename this trip', icon: <IconPencil className="h-4 w-4" />, onSelect: onRename },
    ...others.map((trip, i) => ({
      key: `trip-${trip.id}`,
      group: i === 0 ? 'Open a saved trip' : undefined,
      label: trip.name,
      hint: trip.localOnly
        ? 'On this device'
        : `${trip.stops?.length ?? 0} stop${(trip.stops?.length ?? 0) === 1 ? '' : 's'}`,
      icon: <IconFolder className="h-4 w-4" />,
      onSelect: () => onLoadTrip(trip.id),
    })),
    ...(onSeeAll && others.length
      ? [{ key: 'all', separated: true, label: 'All saved trips', icon: <IconList className="h-4 w-4" />, onSelect: onSeeAll }]
      : []),
  ];
}

/**
 * The ⌄ beside the trip name. Only there when you have another trip to
 * switch to: renaming alone is a click on the name.
 */
export function TripNameMenu({
  savedTrips,
  activeTripId,
  onLoadTrip,
  onRename,
  onSeeAll,
  onImage = false,
}: {
  savedTrips: StoredTrip[];
  activeTripId: string | null;
  onLoadTrip: (id: string) => void;
  onRename: () => void;
  onSeeAll?: () => void;
  onImage?: boolean;
}) {
  const hasOthers = savedTrips.some((t) => t.id !== activeTripId);
  if (!hasOthers) return null;
  return (
    <Menu
      label="Your trips"
      ariaLabel="Your trips: rename or open another"
      iconOnly
      size="sm"
      placement="bottom"
      align="start"
      sheetOnMobile
      sheetTitle="Your trips"
      icon={<IconChevronDown className="h-4 w-4" />}
      testId="trip-name-menu"
      triggerClassName={
        onImage
          ? '!h-7 !w-7 !rounded-full !text-white/90 hover:!bg-black/30 hover:!text-white'
          : '!h-7 !w-7 !text-tp-muted hover:!text-tp-ink'
      }
      items={tripNameMenuItems({ savedTrips, activeTripId, onLoadTrip, onRename, onSeeAll })}
    />
  );
}
